import { cloneElement, isValidElement } from 'react';
import * as ReactVirtualized from 'react-virtualized';

import Grid from '@gogame/ui/Grid';
import Paper from '@gogame/ui/Paper';
import Typography from '@gogame/ui/Typography';
import Tooltip from '@gogame/ui/Tooltip';

const ITEM_HEIGHT = 24;

const rowRenderer = ({ payload, index, dataLabel, percentage, style }) => {
  const {
    dataKey,
    stroke,
    strokeDasharray,
    strokeWidth,
    name,
    value: valuePayload,
    formatter,
    strokeOpacity,
    fill,
  } = payload[index];

  const value = dataLabel ? dataLabel[dataKey] : valuePayload;
  const lineStroke = stroke === 'white' ? fill : stroke;

  const formattedValue = (
    <React.Fragment>
      {formatter
        ? cloneElement(formatter, {
            value: value,
          })
        : value}
      {percentage ? ` (${valuePayload}%)` : null}
    </React.Fragment>
  );

  return (
    <Grid
      container
      key={index}
      spacing={1}
      style={{
        ...style,
      }}
      alignItems={'center'}
      justifyContent={'space-between'}
    >
      <Grid item zeroMinWidth style={{ display: 'flex' }}>
        <svg width={'16'} height={'4'}>
          <line
            x1={'0'}
            y1={'3'}
            x2={'16'}
            y2={'3'}
            stroke={lineStroke}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeOpacity={strokeOpacity}
          />
        </svg>
      </Grid>
      <Grid item grow={1} zeroMinWidth justifyContent={'flex-start'}>
        <div title={name} style={{ minWidth: '0' }}>
          <Typography
            lineClamp={1}
            variant={'caption'}
            sx={{ maxWidth: '165px', wordBreak: 'break-all' }}
          >
            {name}
          </Typography>
        </div>
      </Grid>
      <Grid item>
        <Typography
          variant={'caption'}
          lineClamp={1}
          sx={{ maxWidth: '80px', wordBreak: 'break-all' }}
        >
          {' '}
          <Tooltip
            arrow
            title={formattedValue}
            wrapper={true}
            inlineWrapper={false}
          >
            {formattedValue}
          </Tooltip>
        </Typography>
      </Grid>
    </Grid>
  );
};

const TooltipContent = ({
  active,
  payload,
  label,
  labelFormatter,
  scrollAfterItems = 12,
  data,
  percentage,
}) => {
  if (!active || !payload) {
    return null;
  }

  let formattedLabel = label;

  if (isValidElement(labelFormatter)) {
    formattedLabel = cloneElement(labelFormatter, {
      value: label,
    });
  } else if (typeof labelFormatter === 'function') {
    formattedLabel = labelFormatter({ value: label });
  }

  const dataLabel = Array.isArray(data) ? data[label] : null;

  const payloadVisible = payload.filter(({ strokeOpacity }) => strokeOpacity);

  return (
    <Paper
      elevation={8}
      style={{
        padding: '8px 0 12px 0',
        pointerEvents: 'all',
        width: '340px',
      }}
    >
      <Typography variant={'subtitle1'} sx={{ margin: '8px 16px' }}>
        {formattedLabel}
      </Typography>
      <ReactVirtualized.AutoSizer disableHeight>
        {({ width }) => (
          <ReactVirtualized.List
            style={{
              padding: '0 16px',
              outlineStyle: 'none',
              willChange: 'auto',
            }}
            width={width}
            height={Math.min(
              scrollAfterItems * ITEM_HEIGHT,
              payloadVisible.length * ITEM_HEIGHT
            )}
            rowCount={payloadVisible.length}
            rowHeight={24}
            rowRenderer={({ index, style }) =>
              rowRenderer({
                payload: payloadVisible,
                index,
                dataLabel,
                percentage,
                style,
              })
            }
          />
        )}
      </ReactVirtualized.AutoSizer>
    </Paper>
  );
};

export default TooltipContent;
